import { FX_KINDS, type FxKind } from '../editor/textFx';
import { ALL_SHAPES, shapeById } from '../editor/shapeLib';
import { FONT_CATALOG, fontInfo } from '../fonts/catalog';
import type { BuiltinTemplate, TplEl } from './builtin';

/*
 * ─── AI สร้างเทมเพลต (choices 26 ต.ค. 2569: AI ออกแบบ "โครง" แก้ได้ทุกชิ้น · ทุกคนที่มีสิทธิ์ "รูป AI" ใส่ลงงานตัวเอง ·
 * เติมรูปในช่อง = ช่องติ๊กปิดไว้ก่อน · เพดานเงินกองเดียวกับรูป AI · Gemini 3.8 Flash).
 * Gemini answers with the same pieces as the ready-made templates (src/templates/builtin.ts TplEl), laid out on a frame
 * 1080 units wide; src/templates/build.ts turns them into a page. No DOM, no Fabric: the Worker and the app both use
 * checkLayout() — the same checker on the server and when the app puts it in (BUG-13).
 * Anything that is wrong is either refused with the reason, or put right AND said in `notes` — never dropped silently.
 */

export const LAYOUT_MAX_ELS = 40;
export const LAYOUT_MAX_PHOTOS = 4;
export const LAYOUT_TEXT_MAX = 300;
export const LAYOUT_BRIEF_MAX = 1000;

/** the page sizes the panel offers (px); a work's own size is offered too */
export const LAYOUT_SIZES: Array<{ id: string; th: string; w: number; h: number }> = [
  { id: 'post', th: 'โพสต์สี่เหลี่ยม 1080×1080', w: 1080, h: 1080 },
  { id: 'tall', th: 'โพสต์แนวตั้ง 1080×1350', w: 1080, h: 1350 },
  { id: 'story', th: 'สตอรี่ 1080×1920', w: 1080, h: 1920 },
  { id: 'wide', th: 'แนวนอน 1920×1080', w: 1920, h: 1080 },
  { id: 'a4', th: 'A4 แนวตั้ง (พิมพ์)', w: 2480, h: 3508 }
];

/** fonts with Thai letters: the AI is offered these (a Thai text in a font without Thai is drawn with a stand-in) */
export const LAYOUT_FONTS = FONT_CATALOG.filter(f => f.thai).map(f => f.family);
const FX_IDS = FX_KINDS.map(k => k.id);
const HEX = /^#[0-9a-f]{6}$/i;

/** the JSON schema sent to Gemini (structured output: only keywords Google lists as supported) */
/*
 * Kept simple on purpose (BUG-75, live 26 ต.ค.: Google ended every layout job with INVALID_ARGUMENT). Google's
 * structured-output guide names what makes a schema too complex to serve, and the first schema had three of them:
 * an enum with many values (the 94 shapes), a long run of optional properties (only "k" was required), and maxItems on
 * an array of objects. So: the shape ids are listed in the prompt (an unknown one becomes a rounded box, said), the
 * place and size are required between the rest, and the 40-piece limit is in the prompt and in checkLayout.
 */
export function layoutSchema() {
  const num = { type: 'number' };
  return {
    type: 'object',
    properties: {
      name: { type: 'string', description: 'ชื่อเทมเพลตสั้น ๆ ภาษาไทย' },
      bg: { type: 'string', description: 'page colour #rrggbb' },
      palette: { type: 'array', items: { type: 'string' }, description: '3–5 colours #rrggbb chosen for this design' },
      els: {
        type: 'array', minItems: 1,
        items: {
          type: 'object',
          properties: {
            k: { type: 'string', enum: ['rect', 'ellipse', 'shape', 'line', 'text', 'photo'] },
            x: num, y: num, w: num, h: num, x2: num, y2: num,
            fill: { type: 'string' }, stroke: { type: 'string' }, sw: num, r: num, op: num, angle: num,
            shape: { type: 'string', description: 'one shape id from the list in the instructions' },
            text: { type: 'string' }, font: { type: 'string', enum: LAYOUT_FONTS },
            weight: { type: 'integer', enum: [400, 500, 600, 700] }, size: num, ink: { type: 'string' },
            align: { type: 'string', enum: ['left', 'center', 'right'] }, lh: num,
            fx: { type: 'string', enum: ['none', ...FX_IDS] }, fxColor: { type: 'string' },
            hint: { type: 'string' }, photoShape: { type: 'string', enum: ['rect', 'round', 'circle'] }
          },
          required: ['k', 'x', 'y', 'w', 'h']
        }
      }
    },
    required: ['name', 'bg', 'palette', 'els']
  };
}

/** the instructions sent with the person's words (the frame's size, what each piece means, the rules) */
export function layoutPrompt(brief: string, w: number, h: number, withRef = false): string {
  const H = Math.round(1080 * h / w);
  // รูปตัวอย่าง (26 ต.ค. 17:38): a style to follow, never a design to copy (someone else's work, brand or words)
  const ref = withRef ? [
    'A reference image is attached. Follow its STYLE: layout structure, proportions and white space, hierarchy of text sizes, colour mood, kind of decoration.',
    'Do NOT copy it: never reuse its words, logos, brand names, people, mascots or photos — all content comes from the user\'s request below, and the result must be a new design.',
    'Its size may differ from this frame: adapt the arrangement to this frame. Where the reference shows a photo, put a "photo" slot instead.',
  ] : [];
  return [
    'You are a graphic designer making an editable social-media / print template for a Thai small business.',
    `Design on a frame 1080 units wide and ${H} units tall (the page is ${w}×${h} px). x, y = top-left corner; w, h = size. Keep every piece inside the frame.`,
    'Pieces (field "k"):',
    '- rect: box (x, y, w, h, fill #rrggbb or "transparent", optional stroke + sw for an outline, r = corner radius, op = opacity 0–1, angle)',
    '- ellipse: oval (x, y, w, h, fill, op)',
    '- shape: a decorative shape from the list in "shape" (x, y, w, h, fill, op, angle)',
    '- line: x, y → x2, y2 with ink #rrggbb and sw thickness',
    '- text: x, y, w (box width), h (box height), text, font, weight, size (font size in units, 28–200), ink #rrggbb, align, lh line height 0.9–1.6, optional fx effect with fxColor',
    `- photo: an empty picture slot the user fills later (x, y, w, h, photoShape, hint = a short Thai description of the photo that should go there). At most ${LAYOUT_MAX_PHOTOS}.`,
    'Every piece has x, y, w and h (a line uses x2, y2 and may have w = h = 0).',
    'Text must fit its box — measure before placing: one Thai or Latin character is about 0.55 × size wide, so a line holds about w ÷ (0.55 × size) characters;',
    'lines = the words split at those widths (and at every "\\n"); h must be at least lines × size × lh. If it does not fit, use fewer words, a smaller size or a wider box.',
    'No two text boxes may overlap, and nothing may cover a text except the box drawn behind it as its background.',
    'Design: first choose "palette" — 3–5 colours #rrggbb that suit the brief (a background, a main colour, an accent, a dark and a light text colour).',
    'Use only palette colours, and give every piece its colour: fill for rect/ellipse/shape, ink for text and line, and always a font for text.',
    'Text must contrast strongly with what is behind it. One clear headline (size 90–150), a sub-headline (44–64), details (28–40);',
    'group related information, align pieces to shared left edges or centres, keep even spacing, and keep decorative shapes behind the content (op 0.15–0.5).',
    `Shape ids for "shape" (use only these): ${ALL_SHAPES.map(s => s.id).join(', ')}.`,
    'Rules: all words the customer reads are in Thai (prices, phone placeholders, dates may use digits). Big clear headline, good contrast, 3–5 colours that suit the brief,',
    'leave margins of at least 50 units, no overlapping texts, put pieces in drawing order (back first). Use "\\n" inside text for line breaks.',
    `Use ${LAYOUT_MAX_ELS} pieces or fewer. Do not draw a full-page background rect: set "bg" instead.`,
    ...ref,
    '',
    'What the user wants:',
    brief
  ].join('\n');
}

export type LayoutChecked = { ok: true; value: { name: string; bg: string; els: TplEl[]; notes: string[] } } | { ok: false; error: string };

/** WCAG contrast of two #rrggbb colours (1 = the same, 21 = black on white) */
export function contrast(a: string, b: string): number {
  const lum = (hex: string) => {
    const [r, g, bl] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255).map(c => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [x, y] = [lum(a), lum(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const n = (v: unknown) => typeof v === 'number' && Number.isFinite(v) ? v : NaN;

/**
 * The answer made safe to build, for a page w × h. Refused (with the reason): not an object, no pieces, more than
 * LAYOUT_MAX_ELS, nothing left that can be drawn. Put right and said in notes: an unknown colour, font, shape or effect,
 * a piece reaching past the frame, a text too long, more photo slots than allowed (the extra ones dropped and counted).
 */
export function checkLayout(raw: unknown, w: number, h: number): LayoutChecked {
  if (!isObj(raw)) return { ok: false, error: 'AI ตอบกลับไม่ใช่โครงเทมเพลต (ไม่ใช่ข้อมูลแบบที่ขอ)' };
  if (!Array.isArray(raw.els) || !raw.els.length) return { ok: false, error: 'AI ตอบกลับแต่ไม่มีชิ้นงานในเทมเพลต ลองเปลี่ยนคำอธิบายแล้วสร้างใหม่' };
  if (raw.els.length > LAYOUT_MAX_ELS) return { ok: false, error: `AI ใส่ชิ้นงานมา ${raw.els.length} ชิ้น เกิน ${LAYOUT_MAX_ELS} ชิ้น ลองสร้างใหม่` };
  const H = 1080 * h / w;
  const notes: string[] = [];
  const said = new Set<string>();
  const note = (t: string) => { if (!said.has(t)) { said.add(t); notes.push(t); } };
  // ชุดสีของแบบ (palette): what a missing colour becomes, instead of a grey nobody chose (26 ต.ค.: grey blobs, said nowhere)
  const palette = Array.isArray(raw.palette) ? [...new Set((raw.palette as unknown[]).filter((c): c is string => typeof c === 'string' && HEX.test(c)).map(c => c.toLowerCase()))].slice(0, 8) : [];
  let missing = 0;
  /** a colour the answer gives; none → `fallback()` and counted (said once at the end); not a colour → said */
  const colour = (v: unknown, fallback: () => string, what: string, allowNone = false) => {
    if (allowNone && v === 'transparent') return v;
    if (typeof v === 'string' && HEX.test(v)) return v.toLowerCase();
    const dflt = fallback();
    if (v === undefined || v === null || v === '') missing++;
    else note(`สี "${String(v).slice(0, 20)}" ของ${what}ไม่ถูกรูปแบบ ใช้ ${dflt} แทน`);
    return dflt;
  };
  /** a box kept inside the frame (a little past the edge is allowed for decoration, as the ready-made ones do) */
  const box = (e: Record<string, unknown>, what: string) => {
    let x = n(e.x), y = n(e.y), bw = n(e.w), bh = n(e.h);
    if (![x, y, bw, bh].every(Number.isFinite) || bw < 4 || bh < 4) return null;
    const pad = 200;
    // the size first, then the place (the other way round, a huge box was pushed far off the page)
    if (bw > 1080 + 2 * pad || bh > H + 2 * pad) note(`${what}บางชิ้นใหญ่เกินหน้า ย่อให้แล้ว`);
    bw = Math.min(bw, 1080 + 2 * pad); bh = Math.min(bh, H + 2 * pad);
    const nx = Math.min(Math.max(x, -pad), 1080 + pad - bw), ny = Math.min(Math.max(y, -pad), H + pad - bh);
    if (Math.abs(nx - x) > 1 || Math.abs(ny - y) > 1) note(`${what}บางชิ้นเลยกรอบหน้า ย้ายเข้ามาให้แล้ว`);
    x = nx; y = ny;
    return { x, y, w: bw, h: bh };
  };
  const els: TplEl[] = [];
  let photos = 0, dropped = 0;
  const bg = colour(raw.bg, () => palette[0] ?? '#ffffff', 'พื้นหลัง');
  // for text and lines: the colour that stands out most from the page; for boxes and shapes: a palette colour that shows
  const ink = () => [...palette, '#1d1a2b', '#ffffff'].reduce((a, b) => contrast(b, bg) > contrast(a, bg) ? b : a);
  // …and not a colour the words use, when the palette has another (an orange blob under orange words hides them)
  const wordInks = new Set((raw.els as unknown[]).filter(isObj).filter(e => e.k === 'text' && typeof e.ink === 'string').map(e => String(e.ink).toLowerCase()));
  const shows = palette.filter(c => c !== bg && contrast(c, bg) >= 1.5);
  const paint = () => shows.find(c => !wordInks.has(c)) ?? shows[0] ?? ink();
  for (const e of raw.els) {
    if (!isObj(e)) { dropped++; continue; }
    const op = Number.isFinite(n(e.op)) ? Math.min(1, Math.max(0.05, n(e.op))) : undefined;
    const angle = Number.isFinite(n(e.angle)) ? Math.max(-180, Math.min(180, n(e.angle))) : undefined;
    switch (e.k) {
      case 'rect': case 'ellipse': {
        const b = box(e, 'กล่อง');
        if (!b) { dropped++; continue; }
        if (e.k === 'ellipse') { els.push({ k: 'ellipse', ...b, fill: colour(e.fill, paint, 'วงรี'), op }); continue; }
        const sw = Number.isFinite(n(e.sw)) ? Math.min(60, Math.max(0, n(e.sw))) : 0;
        els.push({ k: 'rect', ...b, fill: colour(e.fill, paint, 'กล่อง', true), r: Number.isFinite(n(e.r)) ? Math.max(0, Math.min(n(e.r), Math.min(b.w, b.h) / 2)) : undefined,
          stroke: sw ? colour(e.stroke, ink, 'เส้นขอบ') : undefined, sw: sw || undefined, op, angle });
        continue;
      }
      case 'shape': {
        const b = box(e, 'รูปทรง');
        if (!b) { dropped++; continue; }
        const s = shapeById(e.shape);
        if (!s) { note(`รูปทรง "${String(e.shape ?? '').slice(0, 20)}" ไม่มีในคลัง ใช้กล่องมุมมนแทน`); els.push({ k: 'rect', ...b, fill: colour(e.fill, paint, 'รูปทรง'), r: Math.min(b.w, b.h) / 4, op, angle }); continue; }
        els.push({ k: 'shape', id: s.id, ...b, fill: colour(e.fill, paint, 'รูปทรง'), op, angle });
        continue;
      }
      case 'line': {
        const pts = [n(e.x), n(e.y), n(e.x2), n(e.y2)];
        if (!pts.every(Number.isFinite)) { dropped++; continue; }
        const cl = (v: number, max: number) => Math.min(max + 50, Math.max(-50, v));
        els.push({ k: 'line', x1: cl(pts[0], 1080), y1: cl(pts[1], H), x2: cl(pts[2], 1080), y2: cl(pts[3], H), ink: colour(e.ink, ink, 'เส้น'),
          sw: Number.isFinite(n(e.sw)) ? Math.min(40, Math.max(1, n(e.sw))) : 4 });
        continue;
      }
      case 'text': {
        const text = typeof e.text === 'string' ? e.text.normalize('NFC').replace(/\r\n?/g, '\n').replace(/\\n/g, '\n').trim() : '';
        if (!text) { dropped++; continue; }
        const chars = [...text];
        const t = chars.length > LAYOUT_TEXT_MAX ? chars.slice(0, LAYOUT_TEXT_MAX).join('') : text;
        if (t !== text) note(`ข้อความบางกล่องยาวเกิน ${LAYOUT_TEXT_MAX} ตัวอักษร ตัดให้เหลือ ${LAYOUT_TEXT_MAX} ตัว (แก้เองได้)`);
        let x = n(e.x), y = n(e.y), bw = n(e.w);
        if (![x, y].every(Number.isFinite)) { dropped++; continue; }
        if (!Number.isFinite(bw) || bw < 40) bw = 1080 - 120;
        const nx = Math.min(Math.max(x, 0), 1080 - Math.min(bw, 1080)), ny = Math.min(Math.max(y, 0), H - 30);
        if (Math.abs(nx - x) > 1 || Math.abs(ny - y) > 1 || bw > 1080) note('ข้อความบางกล่องเลยกรอบหน้า ย้ายเข้ามาให้แล้ว');
        x = nx; y = ny; bw = Math.min(bw, 1080 - x);
        const info = fontInfo(e.font);
        const font = info && (info.thai || !/[\u0E00-\u0E7F]/.test(text)) ? info.family : undefined;
        if (!font) note(e.font === undefined || e.font === '' ? 'ข้อความบางกล่อง AI ไม่ได้ระบุฟอนต์ ใช้ Sarabun'
          : info ? `ฟอนต์ ${info.family} ไม่มีตัวไทย ข้อความไทยใช้ Sarabun แทน`
            : `ฟอนต์ "${String(e.font).slice(0, 24)}" ไม่มีในแอป ใช้ Sarabun แทน`);
        const fam = font ?? 'Sarabun';
        const weights = fontInfo(fam)!.weights;
        const want = Number.isFinite(n(e.weight)) ? n(e.weight) : 400;
        const weight = weights.reduce((a, b) => Math.abs(b - want) < Math.abs(a - want) ? b : a, weights[0]);
        const size = Number.isFinite(n(e.size)) ? Math.min(240, Math.max(20, n(e.size))) : 48;
        const textInk = colour(e.ink, ink, 'ตัวอักษร');
        const fxKind = typeof e.fx === 'string' && (FX_IDS as string[]).includes(e.fx) ? e.fx as FxKind : null;
        if (e.fx !== undefined && e.fx !== 'none' && !fxKind) note(`เอฟเฟกต์ "${String(e.fx).slice(0, 20)}" ไม่มีในแอป ข้อความนั้นจึงไม่มีเอฟเฟกต์`);
        const align = e.align === 'left' || e.align === 'right' ? e.align : 'center';
        const lh = Number.isFinite(n(e.lh)) ? Math.min(1.8, Math.max(0.85, n(e.lh))) : undefined;
        // the box's height (h): the words are made to fit it when the page is built (build.ts), never less than one line
        const hh = n(e.h);
        const fitH = Number.isFinite(hh) && hh > 0 ? Math.max(Math.min(hh, H - y), size * (lh ?? 1.16)) : undefined;
        els.push({ k: 'text', x, y, w: bw, text: t, font: fam, weight, size, ink: textInk, align, lh, fitH,
          fx: fxKind ? { kind: fxKind, ...(e.fxColor !== undefined ? { color: colour(e.fxColor, ink, 'เอฟเฟกต์') } : {}) } : undefined });
        continue;
      }
      case 'photo': {
        const b = box(e, 'ช่องใส่รูป');
        if (!b) { dropped++; continue; }
        if (photos >= LAYOUT_MAX_PHOTOS) { note(`AI ใส่ช่องใส่รูปมาเกิน ${LAYOUT_MAX_PHOTOS} ช่อง ช่องที่เกินไม่ได้ใส่`); continue; }
        photos++;
        const hint = typeof e.hint === 'string' && e.hint.trim() ? [...e.hint.normalize('NFC').trim()].slice(0, 120).join('') : 'รูปสินค้าของคุณ';
        const shape = e.photoShape === 'round' || e.photoShape === 'circle' ? e.photoShape : 'rect';
        els.push({ k: 'photo', ...b, hint, shape });
        continue;
      }
      default: dropped++;
    }
  }
  if (dropped) note(`มีชิ้นงาน ${dropped} ชิ้นที่ข้อมูลไม่ครบหรือไม่รู้จัก จึงไม่ได้ใส่`);
  if (missing) note(palette.length ? `AI ไม่ได้ใส่สีมา ${missing} จุด ใช้สีจากชุดสีของแบบแทน (แก้เองได้)` : `AI ไม่ได้ใส่สีมา ${missing} จุด และไม่ได้ส่งชุดสีมา ใช้สีที่ตัดกับพื้นหลังแทน (แก้เองได้)`);
  if (!els.length) return { ok: false, error: 'AI ตอบกลับแต่ไม่มีชิ้นงานที่ใช้ได้เลย ลองเปลี่ยนคำอธิบายแล้วสร้างใหม่' };
  const name = typeof raw.name === 'string' && raw.name.trim() ? [...raw.name.normalize('NFC').trim()].slice(0, 60).join('') : 'เทมเพลตจาก AI';
  return { ok: true, value: { name, bg, els, notes } };
}

/** a checked layout as a template build.ts can turn into a page */
export const layoutTemplate = (id: string, l: { name: string; bg: string; els: TplEl[] }, w: number, h: number): BuiltinTemplate =>
  ({ id, th: l.name, cat: 'promo', kw: '', w, h, bg: l.bg, els: l.els });

/** the JSON Gemini wrote: its text, with a ```json fence taken off (CORE: AI answers in JSON — parse with care) */
export function layoutJsonOf(text: string): unknown {
  const t = text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
  try { return JSON.parse(t); } catch { return undefined; }
}

/*
 * ─── ใช้ AI อื่น (choices 26 ต.ค. 2569 21:47: ก "วางโครง JSON" · เฉพาะคนที่มีสิทธิ์ "รูป AI") ───
 * The person copies these instructions into another AI app (ChatGPT, Claude, the Gemini app — which take a reference
 * picture), then pastes its answer back. The answer goes through the same checkLayout as Gemini's (BUG-13) and the same
 * builder. Nothing is sent to our server and nothing is paid here.
 */
export const PASTE_MAX_CHARS = 100_000;

/** the instructions to copy: the same rules as Gemini's, the JSON shape spelled out (no structured output there) */
export function externalPrompt(brief: string, w: number, h: number): string {
  const H = Math.round(1080 * h / w);
  const example = { name: 'ชื่อเทมเพลต', bg: '#1d1a2b', palette: ['#1d1a2b', '#ff7a1a', '#ffffff'], els: [
    { k: 'text', x: 60, y: 80, w: 960, h: 140, text: 'หัวข้อ', font: LAYOUT_FONTS[0], weight: 700, size: 110, ink: '#ffffff', align: 'center' },
    { k: 'photo', x: 60, y: 300, w: 960, h: 560, hint: 'รูปสินค้า', photoShape: 'round' }
  ] };
  return [
    layoutPrompt(brief, w, h),
    '',
    'If a reference image is attached, follow its STYLE only (layout, proportions, text sizes, colour mood); never copy its words, logos, brand names, people or photos.',
    `Answer with ONE JSON object and nothing else (no explanation). Frame: 1080 × ${H} units. It must match this JSON schema:`,
    JSON.stringify(layoutSchema()),
    'Example of the shape (not of the design):',
    JSON.stringify(example)
  ].join('\n');
}

/**
 * What the person pasted → the layout object, or the reason in Thai. AI apps wrap JSON in ```json fences or a sentence
 * before/after: the outermost {…} is taken. Not JSON / too long / empty = refused with the reason (never guessed).
 */
export function pastedLayoutOf(text: string): { ok: true; value: unknown } | { ok: false; error: string } {
  const t = text.trim();
  if (!t) return { ok: false, error: 'ยังไม่ได้วางคำตอบจาก AI' };
  if (t.length > PASTE_MAX_CHARS) return { ok: false, error: `คำตอบยาวเกิน ${PASTE_MAX_CHARS.toLocaleString('th-TH')} ตัวอักษร (อาจวางข้อความอื่นมาด้วย)` };
  const a = t.indexOf('{'), b = t.lastIndexOf('}');
  if (a < 0 || b <= a) return { ok: false, error: 'ไม่พบโครง JSON ในข้อความที่วาง (ต้องมีส่วนที่ขึ้นต้นด้วย { และจบด้วย }) ลองขอให้ AI ตอบเป็น JSON อย่างเดียว' };
  try { return { ok: true, value: JSON.parse(t.slice(a, b + 1)) }; }
  catch (e) {
    return { ok: false, error: `โครง JSON ที่วางอ่านไม่ได้ (${e instanceof Error ? e.message : String(e)}) — AI อาจตอบไม่จบหรือคัดลอกมาไม่ครบ ลองคัดลอกใหม่ทั้งคำตอบ` };
  }
}
